import { useState } from "react";
import { Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Reveal } from "./Reveal";

const formatErr = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Si è verificato un errore, riprova";
};

const Input = ({ label, testid, ...props }) => (
  <div>
    <label className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#78716C] block mb-2">{label}</label>
    <input data-testid={testid} {...props} className="w-full rounded-xl border border-[#E7E0D8] bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#2E8A57]" />
  </div>
);

export const B2BAuth = ({ onAuthed }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ company: "", vat: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "login" ? "/b2b/login" : "/b2b/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const r = await api.post(url, body);
      sessionStorage.setItem("b2b_token", r.data.token);
      toast.success(mode === "login" ? "Bentornato!" : "Registrazione inviata!");
      onAuthed(r.data.account);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitLabel = mode === "login" ? "Accedi all'area rivenditori" : "Invia richiesta di registrazione";

  return (
    <div data-testid="b2b-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-14 items-start">
      <Reveal>
        <div className="w-12 h-12 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-6">
          <Briefcase className="w-5 h-5 text-[#2E8A57]" />
        </div>
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Per ristoranti, negozi e distributori</p>
        <h1 className="font-serif-display font-bold text-4xl sm:text-5xl tracking-tight text-[#1C1917] leading-[1.05]">
          Diventa rivenditore <span className="italic text-[#2E8A57]">gluten free.</span>
        </h1>
        <p className="mt-5 text-[#57534E] leading-relaxed max-w-md">
          Registra la tua azienda per accedere al listino rivenditore riservato: prezzi dedicati su tutti i {""}
          piatti pronti certificati, spedizione refrigerata e supporto diretto.
        </p>
        <ul className="mt-7 space-y-3 text-sm text-[#44403C]">
          {["Listino riservato con sconto rivenditore", "79 referenze certificate gluten free", "Consegne refrigerate in tutta Italia", "Approvazione della richiesta in 24/48h"].map((t) => (
            <li key={t} className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7DC242]" /> {t}
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="rounded-2xl border border-[#E7E0D8] bg-white p-7 lg:p-9">
          <div className="flex gap-2 mb-7">
            <button data-testid="b2b-tab-login" onClick={() => setMode("login")} className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${mode === "login" ? "bg-[#2E8A57] text-white border-[#2E8A57]" : "border-[#E7E0D8]"}`}>Accedi</button>
            <button data-testid="b2b-tab-register" onClick={() => setMode("register")} className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${mode === "register" ? "bg-[#2E8A57] text-white border-[#2E8A57]" : "border-[#E7E0D8]"}`}>Registra la tua azienda</button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <Input label="Ragione sociale *" testid="b2b-company-input" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <Input label="Partita IVA *" testid="b2b-vat-input" required minLength={8} value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
                <Input label="Telefono" testid="b2b-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </>
            )}
            <Input label="Email aziendale *" testid="b2b-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input label="Password * (min 8 caratteri)" testid="b2b-password-input" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <button data-testid="b2b-submit-btn" type="submit" disabled={busy} className="btn-tuscan w-full py-4 rounded-full text-sm font-semibold disabled:opacity-40">
              {busy ? "Attendere..." : submitLabel}
            </button>
          </form>
        </div>
      </Reveal>
    </div>
  );
};
